import { useQuery } from "@tanstack/react-query";
import React from "react";
import type { Control, FieldPath, FieldValues } from "react-hook-form";

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components";
import { RequiredLabel } from "@/components/ui/required-label";
import { useI18n } from "@/context/i18n";
import * as paymentGatewaysApi from "@/pages/payment-gateways/payment-gateways.api";

type Props<T extends FieldValues> = {
  control: Control<T>;
  name?: FieldPath<T>;
};

export function PaymentGatewayField<T extends FieldValues>({
  control,
  name = "payment_gateway_id" as FieldPath<T>,
}: Props<T>) {
  const { t } = useI18n();
  const { data: gateways, isLoading } = useQuery({
    queryKey: ["payment-gateways"],
    queryFn: () => paymentGatewaysApi.getPaymentGateways(false),
  });

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <RequiredLabel required>
            {t("products.form.fields.payment_gateway")}
          </RequiredLabel>
          <Select
            disabled={isLoading}
            onValueChange={field.onChange}
            value={field.value || ""}
          >
            <FormControl>
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={t("products.form.fields.payment_gateway_placeholder")}
                />
              </SelectTrigger>
            </FormControl>
            <SelectContent className="max-h-[min(60vh,24rem)]">
              <SelectGroup>
                {(gateways ?? []).map((g) => (
                  <SelectItem key={g.id} value={g.id}>
                    {g.name}{" "}
                    <span className="text-muted-foreground text-xs">({g.slug})</span>
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
